import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  ArrowRightLeft,
  CheckCircle,
  XCircle,
  Truck,
  PackageCheck,
  Clock,
  AlertTriangle,
  Eye,
  RefreshCw,
} from 'lucide-react';
import {
  useTransfers,
  usePendingHQApprovalTransfers,
  useSLADashboard,
  type Transfer,
} from '../../api/queries/transfer.queries';
import { useCancelTransfer } from '../../api/mutations/transfer.mutations';
import { useAuthStore } from '../../stores/auth.store';
import CreateTransferModal from './components/CreateTransferModal';
import TransferDetailModal from './components/TransferDetailModal';
import ApproveTransferModal from './components/ApproveTransferModal';
import DispatchModal from './components/DispatchModal';
import ReceiveTransferModal from './components/ReceiveTransferModal';

type TabType = 'all' | 'pending' | 'in_transit' | 'completed' | 'hq_approval';

function TransfersPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  // Tab and filter state
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<Transfer | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [isHQApproval, setIsHQApproval] = useState(false);

  // Queries - Always fetch all transfers for accurate tab counts
  const { data: transfersData, refetch } = useTransfers({
    status: statusFilter || undefined, // Only filter when explicitly set in the dropdown
    type: typeFilter || undefined,
  });

  const { data: pendingHQTransfers, refetch: refetchHQ } = usePendingHQApprovalTransfers();
  const { data: slaDashboard } = useSLADashboard();

  const allTransfers = transfersData?.transfers || [];

  // Filter transfers client-side based on active tab
  const getFilteredTransfers = () => {
    switch (activeTab) {
      case 'pending':
        return allTransfers.filter(t =>
          ['request', 'pending_hq_approval', 'approved', 'picking', 'pending_dispatch_verification'].includes(t.status)
        );
      case 'in_transit':
        return allTransfers.filter(t =>
          ['in_transit', 'pending_receiving', 'partially_received'].includes(t.status)
        );
      case 'completed':
        return allTransfers.filter(t =>
          ['received', 'completed'].includes(t.status)
        );
      case 'hq_approval':
        return pendingHQTransfers || [];
      default:
        return allTransfers;
    }
  };

  const transfers = getFilteredTransfers();

  // Mutation for cancel (declared at component level)
  const cancelTransfer = useCancelTransfer(selectedTransfer?._id || '');

  const handleRefresh = () => {
    refetch();
    refetchHQ();
  };

  const handleViewDetail = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setShowDetailModal(true);
  };

  const handleApprove = (transfer: Transfer, hqApproval: boolean = false) => {
    setSelectedTransfer(transfer);
    setIsHQApproval(hqApproval);
    setShowApproveModal(true);
    setShowDetailModal(false);
  };

  const handleDispatch = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setShowDispatchModal(true);
    setShowDetailModal(false);
  };

  const handleReceive = (transfer: Transfer) => {
    setSelectedTransfer(transfer);
    setShowReceiveModal(true);
    setShowDetailModal(false);
  };

  const handleCancel = async (_transfer: Transfer) => {
    const reason = prompt(t('transfers.cancelReason'));
    if (!reason) return;

    try {
      await cancelTransfer.mutateAsync(reason);
      refetch();
    } catch (error) {
      console.error('Failed to cancel transfer:', error);
    }
  };

  const handleModalSuccess = () => {
    setShowCreateModal(false);
    setShowApproveModal(false);
    setShowDispatchModal(false);
    setShowReceiveModal(false);
    setShowDetailModal(false);
    setSelectedTransfer(null);
    handleRefresh();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'request':
      case 'pending_hq_approval':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'approved':
      case 'picking':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending_dispatch_verification':
      case 'in_transit':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'pending_receiving':
      case 'partially_received':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'received':
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'rejected':
      case 'cancelled':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'request':
      case 'pending_hq_approval':
        return <Clock className="h-4 w-4" />;
      case 'approved':
      case 'picking':
      case 'pending_dispatch_verification':
        return <CheckCircle className="h-4 w-4" />;
      case 'in_transit':
        return <Truck className="h-4 w-4" />;
      case 'pending_receiving':
      case 'partially_received':
      case 'received':
      case 'completed':
        return <PackageCheck className="h-4 w-4" />;
      case 'rejected':
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <ArrowRightLeft className="h-4 w-4" />;
    }
  };

  const getSLAColor = (status?: string) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 bg-green-50';
      case 'at_risk':
        return 'text-orange-600 bg-orange-50';
      case 'breached':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'warehouse_to_branch':
        return t('transfers.warehouseToBranch');
      case 'branch_to_branch':
        return t('transfers.branchToBranch');
      case 'branch_to_warehouse':
        return t('transfers.branchToWarehouse');
      default:
        return type;
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const canApprove = (transfer: Transfer) => {
    if (transfer.status === 'pending_hq_approval') {
      return user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING';
    }
    if (transfer.status === 'request') {
      return user?.role === 'WAREHOUSE_STAFF' || user?.role === 'HQ_ADMIN';
    }
    return false;
  };

  const canDispatch = (transfer: Transfer) => {
    return (
      ['approved', 'picking', 'pending_dispatch_verification'].includes(transfer.status) &&
      (user?.role === 'WAREHOUSE_STAFF' || user?.role === 'HQ_ADMIN')
    );
  };

  const canReceive = (transfer: Transfer) => {
    return (
      ['in_transit', 'pending_receiving', 'partially_received'].includes(transfer.status) &&
      (user?.role === 'BRANCH_MANAGER' || user?.role === 'BRANCH_SUPERVISOR' || user?.role === 'WAREHOUSE_STAFF' || user?.role === 'HQ_ADMIN')
    );
  };

  const canCreate = user?.role === 'BRANCH_MANAGER' || user?.role === 'WAREHOUSE_STAFF' || user?.role === 'HQ_ADMIN';

  const tabs = [
    { id: 'all', label: t('common.all'), count: allTransfers.length },
    { id: 'pending', label: t('transfers.pending'), count: allTransfers.filter(t => ['request', 'pending_hq_approval', 'approved', 'picking', 'pending_dispatch_verification'].includes(t.status)).length },
    { id: 'in_transit', label: t('transfers.inTransit'), count: allTransfers.filter(t => ['in_transit', 'pending_receiving', 'partially_received'].includes(t.status)).length },
    { id: 'completed', label: t('transfers.completed'), count: allTransfers.filter(t => ['received', 'completed'].includes(t.status)).length },
  ];

  // Add HQ approval tab for HQ users
  if (user?.role === 'HQ_ADMIN' || user?.role === 'HQ_PURCHASING') {
    tabs.push({
      id: 'hq_approval',
      label: t('transfers.hqApproval'),
      count: pendingHQTransfers?.length || 0,
    });
  }

  // transfers is already filtered by getFilteredTransfers(), including hq_approval tab
  const displayTransfers = transfers;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('transfers.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('transfers.subtitle')}</p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* SLA Dashboard Summary */}
      {slaDashboard && (slaDashboard.summary.atRisk > 0 || slaDashboard.summary.breached > 0) && (
        <div className="bg-card border-b p-4">
          <div className="flex items-center gap-4">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            <span className="text-sm font-medium">{t('transfers.slaAlerts')}:</span>
            {slaDashboard.summary.atRisk > 0 && (
              <span className="px-2 py-1 rounded bg-orange-100 text-orange-700 text-xs">
                {slaDashboard.summary.atRisk} {t('transfers.atRisk')}
              </span>
            )}
            {slaDashboard.summary.breached > 0 && (
              <span className="px-2 py-1 rounded bg-red-100 text-red-700 text-xs">
                {slaDashboard.summary.breached} {t('transfers.breached')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b bg-card">
        <div className="flex gap-1 p-2 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'hover:bg-secondary'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-xs ${
                  activeTab === tab.id
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-secondary text-secondary-foreground'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium">{t('transfers.type')}:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border bg-background px-3 py-1.5 text-sm"
          >
            <option value="">{t('common.all')}</option>
            <option value="warehouse_to_branch">{t('transfers.warehouseToBranch')}</option>
            <option value="branch_to_branch">{t('transfers.branchToBranch')}</option>
            <option value="branch_to_warehouse">{t('transfers.branchToWarehouse')}</option>
          </select>

          {activeTab === 'all' && (
            <>
              <label className="text-sm font-medium ml-4">{t('transfers.status')}:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border bg-background px-3 py-1.5 text-sm"
              >
                <option value="">{t('common.all')}</option>
                <option value="request">{t('transfers.request')}</option>
                <option value="pending_hq_approval">{t('transfers.pending_hq_approval')}</option>
                <option value="approved">{t('transfers.approved')}</option>
                <option value="in_transit">{t('transfers.in_transit')}</option>
                <option value="received">{t('transfers.received')}</option>
                <option value="completed">{t('transfers.completed')}</option>
                <option value="rejected">{t('transfers.rejected')}</option>
                <option value="cancelled">{t('transfers.cancelled')}</option>
              </select>
            </>
          )}
        </div>

        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('transfers.createTransfer')}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto space-y-3">
          {displayTransfers.map((transfer) => {
            const fromLocation = typeof transfer.fromLocationId === 'object' ? transfer.fromLocationId : null;
            const toLocation = typeof transfer.toLocationId === 'object' ? transfer.toLocationId : null;

            return (
              <div
                key={transfer._id}
                className="rounded-lg border bg-card p-4 hover:bg-secondary/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Status Icon */}
                  <div className="mt-1">{getStatusIcon(transfer.status)}</div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium">{transfer.transferNumber}</h3>
                          <span
                            className={`text-xs px-2 py-0.5 rounded border ${getStatusColor(transfer.status)}`}
                          >
                            {t(`transfers.${transfer.status}`)}
                          </span>
                          {transfer.slaTracking?.slaStatus && transfer.slaTracking.slaStatus !== 'on_track' && (
                            <span className={`text-xs px-2 py-0.5 rounded ${getSLAColor(transfer.slaTracking.slaStatus)}`}>
                              {t(`transfers.sla_${transfer.slaTracking.slaStatus}`)}
                            </span>
                          )}
                          {transfer.isReplacementTransfer && (
                            <span className="text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-700">
                              {t('transfers.replacement')}
                            </span>
                          )}
                          {transfer.hasRejectedItems && (
                            <span className="text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {t('transfers.hasRejections')}
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-muted-foreground mt-1">
                          {getTypeLabel(transfer.type)}
                        </div>

                        <div className="text-xs text-muted-foreground mt-1">
                          {fromLocation?.name || '-'} → {toLocation?.name || '-'}
                        </div>

                        <div className="text-xs text-muted-foreground mt-1">
                          {transfer.items.length} {t('pos.items')} • {formatDate(transfer.requestedDate)}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleViewDetail(transfer)}
                          className="rounded px-3 py-1 text-xs bg-secondary hover:bg-secondary/80 flex items-center gap-1"
                        >
                          <Eye className="h-3 w-3" />
                          {t('common.view')}
                        </button>

                        {canApprove(transfer) && (
                          <button
                            onClick={() => handleApprove(transfer, transfer.status === 'pending_hq_approval')}
                            className="rounded px-3 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            {t('transfers.approve')}
                          </button>
                        )}

                        {canDispatch(transfer) && (
                          <button
                            onClick={() => handleDispatch(transfer)}
                            className="rounded px-3 py-1 text-xs bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1"
                          >
                            <Truck className="h-3 w-3" />
                            {t('transfers.dispatch')}
                          </button>
                        )}

                        {canReceive(transfer) && (
                          <button
                            onClick={() => handleReceive(transfer)}
                            className="rounded px-3 py-1 text-xs bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-1"
                          >
                            <PackageCheck className="h-3 w-3" />
                            {t('transfers.receive')}
                          </button>
                        )}

                        {['request', 'pending_hq_approval', 'approved'].includes(transfer.status) && (
                          <button
                            onClick={() => handleCancel(transfer)}
                            className="rounded px-3 py-1 text-xs bg-destructive/10 text-destructive hover:bg-destructive/20 flex items-center gap-1"
                          >
                            <XCircle className="h-3 w-3" />
                            {t('common.cancel')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {displayTransfers.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <ArrowRightLeft className="h-16 w-16 mx-auto mb-4 opacity-20" />
              <p>{t('transfers.noTransfers')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateTransferModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {showDetailModal && selectedTransfer && (
        <TransferDetailModal
          transfer={selectedTransfer}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTransfer(null);
          }}
          onApprove={canApprove(selectedTransfer) ? () => handleApprove(selectedTransfer, selectedTransfer.status === 'pending_hq_approval') : undefined}
          onDispatch={canDispatch(selectedTransfer) ? () => handleDispatch(selectedTransfer) : undefined}
          onReceive={canReceive(selectedTransfer) ? () => handleReceive(selectedTransfer) : undefined}
        />
      )}

      {showApproveModal && selectedTransfer && (
        <ApproveTransferModal
          transfer={selectedTransfer}
          isHQApproval={isHQApproval}
          onClose={() => {
            setShowApproveModal(false);
            setSelectedTransfer(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {showDispatchModal && selectedTransfer && (
        <DispatchModal
          transfer={selectedTransfer}
          onClose={() => {
            setShowDispatchModal(false);
            setSelectedTransfer(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {showReceiveModal && selectedTransfer && (
        <ReceiveTransferModal
          transfer={selectedTransfer}
          onClose={() => {
            setShowReceiveModal(false);
            setSelectedTransfer(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
}

export default TransfersPage;
